import { useIntercom } from '@/layers/presentation/context/IntercomContext';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { subscribeUser } from '@/layers/presentation/services/users';
import { trackGAEvent } from '@/layers/presentation/components/utils/GAEventTracking';
import logger from '@/layers/support/monitoring/logger';
import { SubscriptionParams, SubscriptionText } from '@/types/users';
import { Button, Checkbox, Input, Link } from '@nextui-org/react';
import { useState } from 'react';
import { z } from 'zod';

const LOG_MODULE = 'FE [Presentation / Components / SubscriptionForm]';

// Type for subscription form data
type SubscriptionFormData = {
  email: string;
  termsAccepted: boolean;
};

interface SubscriptionFormProps {
  text: SubscriptionText;
  params: SubscriptionParams;
  onSubscribeSuccess: () => void;
}

export function SubscriptionForm({ text, params, onSubscribeSuccess }: SubscriptionFormProps) {
  const { t } = useTranslation();
  const { getVisitorId } = useIntercom();
  const [email, setEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Create localized validation schema
  const createValidationSchema = () =>
    z.object({
      email: z
        .string()
        .min(1, t('components:subscriptionForm.emailRequired') || 'Email is required')
        .email(
          t('components:subscriptionForm.invalidEmailFormat') ||
            'Please enter a valid email address',
        ),
      termsAccepted: z
        .boolean()
        .refine(
          (val: boolean) => val === true,
          t('components:subscriptionForm.termsRequired') ||
            'You must accept the terms and conditions',
        ),
    });

  logger.debug(`${LOG_MODULE}`, { params, email, termsAccepted, error, isSubscribing });

  const handleSubmit = async (e: React.FormEvent) => {
    const visitorId = await getVisitorId();
    logger.debug(`${LOG_MODULE} handleSubmit`, { e, email, termsAccepted, params, visitorId });

    e.preventDefault();
    setError('');

    // Zod validation with localized messages
    const validationSchema = createValidationSchema();
    const validationResult = validationSchema.safeParse({
      email,
      termsAccepted,
    });

    if (!validationResult.success) {
      const firstError = validationResult.error.errors[0];
      setError(firstError.message);
      return;
    }

    try {
      setIsSubscribing(true);
      const success = await subscribeUser(email, { ...params, visitorId });
      if (success) {
        // Track subscription event
        const eventName = params.programId ? 'program_inbox_subscribe' : 'newsletter_subscribe';
        trackGAEvent(eventName, {
          lead_stage: 'micro',
          program_id: params.programId || undefined,
        });
        onSubscribeSuccess();
      } else {
        setError(t('components:subscriptionForm.subscriptionFailed'));
      }
    } catch (error) {
      logger.error(`${LOG_MODULE} handleSubmit`, { error });
      setError(t('components:subscriptionForm.errorOccurred'));
    } finally {
      setIsSubscribing(false);
    }
  };

  // Determine form ID based on context
  const formId = params.programId ? 'program-inbox-form' : 'newsletter-form';

  return (
    <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="mb-0 p-0">{text.title}</h2>
      <p>{text.text}</p>
      <Input
        type="email"
        label={t('components:subscriptionForm.email')}
        value={email}
        onChange={e => setEmail(e.target.value)}
        isInvalid={!!error}
        required
      />
      <Checkbox isSelected={termsAccepted} onValueChange={setTermsAccepted}>
        {t('components:subscriptionForm.agreeTerms')}{' '}
        <Link
          href="/about/terms"
          className="text-primary font-semibold underline hover:text-primary-600 transition-colors"
        >
          {t('components:subscriptionForm.termsAndConditions')}
        </Link>
      </Checkbox>
      {error && <p className="text-red-500">{error}</p>}
      <Button
        size="lg"
        color="primary"
        type="submit"
        isDisabled={isSubscribing}
        isLoading={isSubscribing}
      >
        {isSubscribing ? t('components:subscriptionForm.working') : text.button}
      </Button>
    </form>
  );
}
